import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import MarkdownViewer from '../MarkdownViewer';
import { Components } from 'react-markdown';
import isIframeDomainAllowed from '../../lib/isIframeDomainAllowed';

interface HtmlComponentProps {
    html?: string | null;
    isMarkdown?: boolean;
    components?: Partial<Components>;
    isArticle?: boolean; // New prop to enable TOC tracking
}

const HtmlComponent: React.FC<HtmlComponentProps> = ({
    html,
    isMarkdown,
    components,
    isArticle = false
}) => {
    const contentRef = useRef<HTMLSpanElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        const isValidScheme = (url: string) => {
            const parts = url.split(':');
            if (parts.length < 2) {
                return false;
            }
            const scheme = parts[0];
            if (parts[1].startsWith('//')) {
                // Handle protocols like 'myapp://'
                return true;
            }
            return (
                scheme === 'http' ||
                scheme === 'https' ||
                scheme === 'mailto' ||
                scheme === 'tel' ||
                scheme === 'sms'
            );
        };

        const links = contentRef.current.querySelectorAll('a');
        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLAnchorElement;
            const href = target.getAttribute('href');
            if (!href) {
                return;
            }

            // Check if the href starts with 'http:' or 'https:' or 'mailto:' or 'tel:' e.t.c
            if (isValidScheme(href)) {
                // It's a fully qualified link, force such to open in a new tab/window:
                event.preventDefault();
                window.open(href, '_blank');
            } else if (href.startsWith('#')) {
                // It's a hash link, do nothing
                // These hash links are part of the articles service Table of Contents
                return;
            } else {
                // It's a relative link, intercept and use navigate
                event.preventDefault();
                navigate(href);
            }
        };

        links.forEach((link) => {
            link.addEventListener('click', handleClick as EventListener);
        });
        let handleScroll: () => void;
        let positionTOC: () => void;
        if (isArticle) {
            const SIDEBAR_GAP = 50; // Gap between sidebar and article card border
            const TOC_WIDTH = 280;

            // Position TOC to align with article card and enable sticky behavior
            positionTOC = () => {
                const toc = contentRef.current?.querySelector(
                    '.mce-toc'
                ) as HTMLElement;
                const articleCard = document.querySelector(
                    '.answer-card'
                ) as HTMLElement;

                if (toc && articleCard) {
                    const articleCardRect = articleCard.getBoundingClientRect();
                    const articleCardTop = articleCardRect.top;
                    const articleCardLeft = articleCardRect.left;

                    // Set vertical position aligned with article card top
                    toc.style.top =
                        articleCardTop < 0 ? '1px' : `${articleCardTop}px`;

                    // Position 50px to the left of the article card border
                    const leftPosition =
                        articleCardLeft - SIDEBAR_GAP - TOC_WIDTH;
                    toc.style.left = `${Math.max(leftPosition, 20)}px`;

                    toc.classList.add('sticky');
                    toc.classList.add('aligned');
                }
            };

            // Highlight current section in TOC based on scroll position
            const updateTOCActiveState = () => {
                const toc = contentRef.current?.querySelector('.mce-toc');
                if (!toc) return;

                const tocLinks = toc.querySelectorAll(
                    'a[href^="#"]'
                ) as NodeListOf<HTMLAnchorElement>;
                const headings: HTMLElement[] = [];

                // Find all headings that correspond to TOC links
                tocLinks.forEach((link) => {
                    const href = link.getAttribute('href');
                    if (href) {
                        const targetId = href.substring(1);
                        const heading = document.getElementById(targetId);
                        if (heading) {
                            headings.push(heading);
                        }
                    }
                });

                if (headings.length === 0) return;

                // Find the currently visible heading using more accurate positioning
                const viewportTop = window.scrollY;
                const viewportCenter = viewportTop + 10;
                let activeHeading: HTMLElement | null = null;

                // Create array of headings with their positions
                const headingsWithPositions = headings.map((heading) => {
                    const rect = heading.getBoundingClientRect();
                    return {
                        element: heading,
                        top: rect.top + viewportTop,
                        id: heading.id
                    };
                });

                // Sort by position
                headingsWithPositions.sort((a, b) => a.top - b.top);

                // Find the heading that's currently most visible
                for (let i = headingsWithPositions.length - 1; i >= 0; i--) {
                    const heading = headingsWithPositions[i];
                    if (viewportCenter >= heading.top - 50) {
                        // 50px offset for better UX
                        activeHeading = heading.element;
                        break;
                    }
                }

                // If no heading found, default to first one if we're near the top
                if (
                    !activeHeading &&
                    headingsWithPositions.length > 0 &&
                    viewportTop < 200
                ) {
                    activeHeading = headingsWithPositions[0].element;
                }

                // Update active states in TOC
                tocLinks.forEach((link) => {
                    link.classList.remove('active');
                    if (activeHeading) {
                        const href = link.getAttribute('href');
                        if (href === `#${activeHeading.id}`) {
                            link.classList.add('active');
                        }
                    }
                });
            };

            // Reposition on window resize
            window.addEventListener('resize', positionTOC);

            // Position TOC after content loads, and scroll to a ToC anchor in the URL now that
            // its target heading actually exists in the DOM (a fresh page load races the
            // browser's native hash-scroll against this content still being fetched).
            setTimeout(() => {
                positionTOC();
                updateTOCActiveState();

                if (window.location.hash) {
                    const targetId = window.location.hash.substring(1);
                    document
                        .getElementById(targetId)
                        ?.scrollIntoView({
                            behavior: 'instant',
                            block: 'start'
                        });
                }
            }, 100);

            // Create throttled scroll handler for updating active state only
            // The sticky positioning is handled by CSS
            let scrollTimeout: NodeJS.Timeout;
            handleScroll = () => {
                clearTimeout(scrollTimeout);
                positionTOC();
                scrollTimeout = setTimeout(() => {
                    updateTOCActiveState();
                }, 10);
            };

            // Update on scroll - only for updating active states, positioning is handled by CSS sticky
            window.addEventListener('scroll', handleScroll);
        }

        return () => {
            links.forEach((link) => {
                link.removeEventListener('click', handleClick as EventListener);
            });

            if (isArticle && handleScroll && positionTOC) {
                window.removeEventListener('resize', positionTOC);
                window.removeEventListener('scroll', handleScroll);
            }
        };
    }, [navigate, isArticle]);

    if (!html) {
        return null;
    }

    const createMarkup = (htmlString: string) => {
        DOMPurify.addHook('uponSanitizeElement', function (node, data) {
            if (data.tagName === 'iframe') {
                // Check if the iframe's `src` attribute matches the allowed pattern
                const el = node as Element;
                const srcAttr = el.getAttribute('src');
                if (!srcAttr) {
                    //  Remove iframe if no src attribute
                    return node.parentNode?.removeChild(node);
                }
                // Change this to use startsWith where possible
                if (!isIframeDomainAllowed(srcAttr)) {
                    // Remove iframe if src attribute doesn't match explicit url pattern from the above sources
                    return node.parentNode?.removeChild(node);
                }

                // Adds title which is required for accessibility
                const title = el.getAttribute('title');
                if (!title) {
                    el.setAttribute('title', 'Embedded content');
                }

                // Add security attributes and youtube fullscreen
                el.setAttribute('sandbox', 'allow-scripts allow-same-origin');
                el.setAttribute('allowfullscreen', '');
                el.setAttribute(
                    'referrerpolicy',
                    'strict-origin-when-cross-origin'
                );
            }
        });

        return {
            __html: DOMPurify.sanitize(htmlString, {
                ADD_TAGS: ['iframe'],
                ADD_ATTR: ['sandbox', 'allowfullscreen', 'referrerpolicy']
            })
        };
    };

    if (isMarkdown) {
        return (
            <MarkdownViewer
                components={{
                    ...components // User-provided overrides take precedence
                }}
                content={html}
            ></MarkdownViewer>
        );
    }

    return (
        <span ref={contentRef} dangerouslySetInnerHTML={createMarkup(html)} />
    );
};

export default HtmlComponent;
